import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import FormSpacer from "@saleor/components/FormSpacer";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import { StoreOrderLineInput } from "@saleor/types/globalTypes";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { useIntl } from "react-intl";

import { StoreOrderProductList_search_edges } from "../../types/StoreOrderProductList";

export interface StoreOrderAddProductProps extends FetchMoreProps {
  open: boolean;
  onClose: any;
  loading: boolean;
  products: StoreOrderProductList_search_edges[];
  onSubmitAddProduct: (data: StoreOrderLineInput) => void;
  onFetch: (value: string) => void;
}

const useStyles = makeStyles(
  () => ({
    buttonActions: {
      display: "flex",
      width: "100%"
    },
    buttonBack: {
      background: "#FFFFFF !important",
      border: "1px solid #E5E5E5 !important",
      margin: "3px",
      width: "100%"
    },
    buttonBackLabel: {
      color: "#002972"
    },
    buttonWidth: {
      margin: "3px",
      width: "100%"
    },
    singleVariant: {
      alignContent: "center",
      alignItems: "center",
      display: "flex",
      justifyContent: "space-between"
    },
    variantAmount: {
      fontSize: "16px",
      fontWeight: 600,
      marginLeft: "Auto",
      marginRight: "3em",
      marginTop: "8px"
    },
    variantDetails: {
      display: "flex"
    },
    variants: {
      marginLeft: "32px"
    }
  }),
  { name: "StoreOrderAddProduct" }
);

const StoreOrderAddProduct: React.SFC<StoreOrderAddProductProps> = props => {
  const {
    open,
    onClose,
    products,
    onSubmitAddProduct,
    onFetch,
    onFetchMore,
    hasMore,
    loading
  } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const [showResult, setShowResult] = React.useState(false);
  const [productSelected, setProductSelected] = React.useState("");
  const [variantSelected, setVariantSelected] = React.useState("");
  const scrollableDialogClasses = useScrollableDialogStyle({});
  const container = React.useRef<HTMLDivElement>();

  const [Query, onQueryChange] = useSearchQuery(onFetch);

  const handleProductChange = (
    variant: { id: string },
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProductSelected((event.target as HTMLInputElement).value);

    if (variant && variant.hasOwnProperty("id")) {
      setVariantSelected(variant.id);
    } else {
      setVariantSelected("");
    }
  };

  const handleVariantChange = (
    productId,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProductSelected(productId);
    setVariantSelected((event.target as HTMLInputElement).value);
  };

  const handleSubmit = () => {
    onSubmitAddProduct({
      quantity: 1,
      variantId: variantSelected
    });
  };

  const containerHeight = container.current?.scrollHeight - 130;

  React.useEffect(() => {
    onQueryChange({ target: { name: "query", value: "" } });
    setShowResult(false);
  }, []);

  const handleQueryChange = e => {
    if (e.target.value === "") {
      onQueryChange(e);
      setShowResult(false);
    } else {
      setShowResult(true);
      onQueryChange(e);
    }
  };

  const formatter = new Intl.NumberFormat("en-PH", {
    currency: "PHP",
    currencyDisplay: "symbol",
    minimumFractionDigits: 2,
    style: "currency"
  });

  const formatCurrency = (price: number): string => formatter.format(price);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <>
        <DialogTitle>Add Product</DialogTitle>
        <DialogContent>
          <TextField
            name="query"
            onChange={handleQueryChange}
            label={intl.formatMessage({
              defaultMessage: "Search Product"
            })}
            defaultValue={Query}
            placeholder={intl.formatMessage({
              defaultMessage: "Search by product name"
            })}
            fullWidth
            InputProps={{
              autoComplete: "off",
              endAdornment: loading && <CircularProgress size={16} />
            }}
          />
          <FormSpacer />
          <div
            className={scrollableDialogClasses.scrollArea}
            style={{ height: containerHeight }}
          >
            {" "}
            <InfiniteScroll
              pageStart={0}
              loadMore={onFetchMore}
              hasMore={hasMore}
              useWindow={false}
              loader={
                <div
                  className={scrollableDialogClasses.loadMoreLoaderContainer}
                >
                  <CircularProgress size={16} />
                </div>
              }
              threshold={10}
            >
              {showResult
                ? products.map((product, index) => {
                    const { id: productId, name, variants } = product.node;
                    const checkVariant = variants.length !== 0 ? variants : [];
                    return (
                      <RadioGroup
                        key={index}
                        value={productSelected}
                        onChange={e => handleProductChange(checkVariant[0], e)}
                      >
                        {variants.length === 1 ? (
                          <>
                            <div className={classes.singleVariant}>
                              {" "}
                              <FormControlLabel
                                value={productId}
                                control={<Radio />}
                                label={name}
                              />
                              <span
                                style={{
                                  fontSize: "16px",
                                  fontWeight: 600,
                                  marginRight: "3em"
                                }}
                              >
                                {formatCurrency(variants[0].price?.amount)}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <FormControlLabel
                              value={productId}
                              control={<Radio />}
                              label={name}
                            />
                            <div className={classes.variants}>
                              {variants.map((variant, index) => {
                                const { id: variantId, name, price } = variant;
                                return (
                                  <RadioGroup
                                    key={index}
                                    value={variantSelected}
                                    onChange={e =>
                                      handleVariantChange(productId, e)
                                    }
                                  >
                                    <div className={classes.variantDetails}>
                                      <FormControlLabel
                                        value={variantId}
                                        control={<Radio />}
                                        label={name}
                                      />

                                      <span className={classes.variantAmount}>
                                        {formatCurrency(price.amount)}
                                      </span>
                                    </div>
                                  </RadioGroup>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </RadioGroup>
                    );
                  })
                : null}
            </InfiniteScroll>
          </div>
        </DialogContent>
        <DialogActions>
          <div className={classes.buttonActions}>
            <Button
              onClick={onClose}
              variant="contained"
              color="default"
              className={classes.buttonBack}
            >
              <span className={classes.buttonBackLabel}>BACK</span>
            </Button>
            <Button
              autoFocus
              disabled={productSelected === "" ? true : false}
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              className={classes.buttonWidth}
            >
              APPLY
            </Button>
          </div>
        </DialogActions>
      </>
    </Dialog>
  );
};

export default StoreOrderAddProduct;
